import { Injectable } from '@angular/core';
import { LogPublishersService } from "./log-publishers.service";
import { LogPublisher } from "./log-publishers";


// ****************************************************
// Log Level Enumeration -- RFC5424 
// severity of all levels is assumed to be numerically 
// ascending from most important to least important.
// ****************************************************
export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  HTTP = 3,
  VERBOSE = 4,
  DEBUG = 5,
  SILLY = 6,
  ALL = 7,
  OFF = 8
}

// ****************************************************
// Log Entry Class
// ****************************************************
export class LogEntry {
  // Public Properties
  entryDate: Date = new Date();
  message: string = "";
  level: LogLevel = LogLevel.DEBUG;
  extraInfo: any[] = [];
  logWithDate: boolean = true;

  // **************
  // Public Methods
  // **************
  buildLogString(): string {
    let value: string = "";

    if (this.logWithDate) {
      value = new Date() + " - ";
    }
    value += "Type: " + LogLevel[this.level];
    value += " - Message: " + this.message;
    if (this.extraInfo.length) {
      value += " - Extra Info: "
        + this.formatParams(this.extraInfo);
    }

    return value;
  }

  // ***************
  // Private Methods
  // ***************
  private formatParams(params: any[]): string {
    let ret: string = params.join(",");

    // Is there at least one object in the array?
    if (params.some(p => typeof p == "object")) {
      ret = "";
      // Build comma-delimited string
      for (let item of params) {
        ret += JSON.stringify(item) + ",";
      }
    }

    return ret;
  }
}

// ****************************************************
// Log Service Class
// ****************************************************
@Injectable()
export class LogService {

  // Public Properties
  publishers: LogPublisher[];
  level: LogLevel = LogLevel.ALL;
  logWithDate: boolean = true;

  constructor(private publishersService: LogPublishersService) {
    // Set publishers
    this.publishers = this.publishersService.publishers;
  }

  silly(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.SILLY, optionalParams);
  }

  debug(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.DEBUG, optionalParams);
  }

  verbose(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.VERBOSE, optionalParams);
  }

  http(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.HTTP, optionalParams);
  }

  info(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.INFO, optionalParams);
  }

  warn(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.WARN, optionalParams);
  }

  error(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.ERROR, optionalParams);
  }


  log(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.ALL, optionalParams);
  }

  clear(): void {
    for (let logger of this.publishers) {
      logger.clear()
        .subscribe(response => console.log(response));
    }
  }
  // *************************
  // Private methods
  // *************************
  private shouldLog(level: LogLevel): boolean {
    let ret: boolean = false;

    if ((level <= this.level &&
      level !== LogLevel.OFF) ||
      this.level === LogLevel.ALL) {
      ret = true;
    }

    return ret;
  }


  private writeToLog(msg: string, level: LogLevel, params: any[]) {
    if (this.shouldLog(level)) {
      // Declare variables
      let entry: LogEntry = new LogEntry();

      // Build Log Entry
      entry.message = msg;
      entry.level = level;
      entry.extraInfo = params;
      entry.logWithDate = this.logWithDate;

      for (let logger of this.publishers) {
        logger.log(entry)
          .subscribe(response => console.log(response));
      }
    }
  }
}
